import { useMemo, useCallback, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import _ from "lodash"

import { IFeature } from "interfaces"
import {
  GTM_VIEW_DINING_CATEGORY,
  Q_ORDINAL,
  Q_SUB_CATEGORY_ID,
  Q_VENUE,
} from "constant"

import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useVenueController } from "hooks/venue/useVenueController"

import { pushDataLayer } from "utils/googletagmanager/pushDataLayer"

const DEFAULT_FILTER_VALUE = "ALL"

export const useDining = (occupants: IFeature[]) => {
  const { isMultiVenueProject, levels, venues, findFeatureById } = useVenue()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const defaultVenueFilter = useMemo(
    () => locationVenueId || DEFAULT_FILTER_VALUE,
    [locationVenueId]
  )

  // Query String filters
  const subCategoryId = useMemo(
    () => searchParams.get(Q_SUB_CATEGORY_ID) || DEFAULT_FILTER_VALUE,
    [searchParams]
  )

  const ordinalFilter = useMemo(() => {
    const ordinalParam = searchParams.get(Q_ORDINAL)
    const ordinal = Number(ordinalParam)
    return !_.isNil(ordinalParam) && !_.isNaN(ordinal)
      ? ordinal
      : DEFAULT_FILTER_VALUE
  }, [searchParams])

  // Filtered occupant with available anchor(occupied unit or kiosk) under category
  const operatingRestaurantOccupants: IFeature[] = useMemo(
    () =>
      occupants.filter(
        ({ properties }) =>
          properties.anchor && properties.category === "restaurant"
      ),
    [occupants]
  )

  const viewingVenueOperatingRestaurants = useMemo(
    () =>
      operatingRestaurantOccupants.filter(
        ({ properties }) => properties.venue_id === venueFilter
      ),
    [operatingRestaurantOccupants, venueFilter]
  )

  const restaurantFilteredByCategory = useMemo(
    () =>
      subCategoryId === "ALL"
        ? viewingVenueOperatingRestaurants
        : viewingVenueOperatingRestaurants.filter(({ properties }) =>
            _.some(properties.local_categories, { id: subCategoryId })
          ),
    [subCategoryId, viewingVenueOperatingRestaurants]
  )

  const restaurantFilteredByCategoryAndOrdinal = useMemo(
    () =>
      ordinalFilter === "ALL"
        ? restaurantFilteredByCategory
        : restaurantFilteredByCategory.filter(
            ({ properties }) => properties.ordinal === ordinalFilter
          ),
    [restaurantFilteredByCategory, ordinalFilter]
  )

  const restaurantSubCategories = useMemo(
    () =>
      _(viewingVenueOperatingRestaurants)
        .map((restaurant) => restaurant.properties.local_categories)
        .compact()
        .flatten()
        .uniqBy((category) => category.id)
        .filter((category) => category.properties.parent_id !== null)
        .value(),
    [viewingVenueOperatingRestaurants]
  )

  const levelsWithOccupant = useMemo(
    () =>
      _(restaurantFilteredByCategoryAndOrdinal)
        .uniqBy("properties.ordinal")
        .filter((item) => !_.isNil(item.properties.ordinal))
        .map(({ properties: { ordinal } }) =>
          levels.find((level: IFeature) => level.properties.ordinal === ordinal)
        )
        .sortBy("properties.ordinal")
        .value(),
    [levels, restaurantFilteredByCategoryAndOrdinal]
  )

  const sortedResult = useMemo(
    () =>
      _(restaurantFilteredByCategoryAndOrdinal)
        .orderBy(
          [
            "properties.is_featured",
            "properties.featured_image",
            "properties.logo",
            "properties.name.en",
          ],
          ["desc"]
        )
        .value(),
    [restaurantFilteredByCategoryAndOrdinal]
  )

  const showSubCategoryFilter = useMemo(
    () => restaurantSubCategories.length > 0,
    [restaurantSubCategories]
  )

  const changeSubCategory = useCallback(
    (id) => {
      if (id === null) return
      searchParams.set(Q_SUB_CATEGORY_ID, id)
      navigate(`/dining?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const changeOrdinal = useCallback(
    (id) => {
      if (id === null) return
      searchParams.set(Q_ORDINAL, id)
      navigate(`/dining?${searchParams.toString()}`, { replace: true })
    },
    [searchParams, navigate]
  )

  const changeVenueFilter = useCallback(
    (venueId = defaultVenueFilter) => {
      searchParams.set(Q_VENUE, venueId)
      navigate(`/dining?${searchParams.toString()}`, { replace: true })
    },
    [searchParams, navigate, defaultVenueFilter]
  )

  const resetFilter = useCallback(() => {
    searchParams.set(Q_SUB_CATEGORY_ID, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_VENUE, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_ORDINAL, DEFAULT_FILTER_VALUE)
    navigate(`/dining?${searchParams.toString()}`, { replace: true })
  }, [navigate, searchParams])

  useEffect(() => {
    if (subCategoryId && subCategoryId !== DEFAULT_FILTER_VALUE) {
      pushDataLayer({
        event: GTM_VIEW_DINING_CATEGORY,
        dining_category_id: subCategoryId,
        dining_category_name: _.get(
          findFeatureById(subCategoryId),
          "properties.name.en"
        ),
      })
    }
  }, [findFeatureById, subCategoryId])

  return {
    changeSubCategory,
    changeOrdinal,
    changeVenueFilter,
    resetFilter,
    restaurants: sortedResult,
    categories: restaurantSubCategories,
    currentCategory: subCategoryId,
    levels: levelsWithOccupant,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    showSubCategoryFilter,
  }
}
